import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useBreadcrumb } from './context/BreadcrumbContext';

// URL-prefix → ordered crumb trail. Parent crumbs (Sales / Purchases) link
// to the section's most common entry point so clicking the parent backs
// the user out one level without dead-ending on a non-existent /sales URL.
const TRAIL = {
  '/dashboard': [
    { label: 'Dashboard', icon: 'home' },
  ],
  '/customers': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Customers' },
  ],
  '/vendors': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Vendors' },
  ],
  '/products': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Products' },
  ],
  '/invoices': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Sales', to: '/invoices' },
    { label: 'Invoices' },
  ],
  '/quotations': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Sales', to: '/invoices' },
    { label: 'Quotations' },
  ],
  '/proforma': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Sales', to: '/invoices' },
    { label: 'Proforma Invoices' },
  ],
  '/credit-notes': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Sales', to: '/invoices' },
    { label: 'Credit Notes' },
  ],
  '/einvoice': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Sales', to: '/invoices' },
    { label: 'E-Invoice' },
  ],
  // `/sales/*` routes are the newer additions that don't live at a
  // top-level path. Match on the second URL segment for these.
  '/sales/delivery-challans': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Sales', to: '/invoices' },
    { label: 'Delivery Challans' },
  ],
  '/sales/eway-bills': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Sales', to: '/invoices' },
    { label: 'E-Way Bills' },
  ],
  '/purchase-orders': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Purchases', to: '/purchase-orders' },
    { label: 'Purchase Orders' },
  ],
  '/purchase-invoices': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Purchases', to: '/purchase-orders' },
    { label: 'Purchase Invoices' },
  ],
  '/debit-notes': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Purchases', to: '/purchase-orders' },
    { label: 'Debit Notes' },
  ],
  '/inventory': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Inventory' },
  ],
  '/reports': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Reports' },
  ],
  '/reports/basic': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Reports' },
    { label: 'Basic Reports' },
  ],
  '/reports/gst-compliance': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Reports' },
    { label: 'GST Reports' },
  ],
  '/settings': [
    { label: 'Dashboard', to: '/dashboard', icon: 'home' },
    { label: 'Settings' },
  ],
};

export function Breadcrumbs() {
  const location = useLocation();
  const { extra } = useBreadcrumb();

  // Match against the two-segment path first (e.g. `/sales/delivery-challans`);
  // fall back to the first-segment key for legacy single-path routes.
  const parts = location.pathname.split('/').filter(Boolean);
  const twoSeg = parts.length >= 2 ? `/${parts[0]}/${parts[1]}` : null;
  const oneSeg = '/' + (parts[0] || 'dashboard');
  const base = (twoSeg && TRAIL[twoSeg]) || TRAIL[oneSeg];

  // Don't render on routes we don't have a trail for (e.g. transient
  // navigation states or unknown paths the catch-all is about to redirect).
  if (!base) return null;

  // Append the dynamic extra crumb if a page pushed one (e.g. "#INV-...").
  const trail = extra ? [...base, { label: extra }] : base;

  return (
    <nav
      className="mb-4 -mt-2"
      aria-label="Breadcrumb"
      data-testid="breadcrumbs"
    >
      <ol className="flex items-center flex-wrap gap-1 text-sm">
        {trail.map((crumb, idx) => {
          const isLast = idx === trail.length - 1;
          const content = (
            <span className="inline-flex items-center gap-1.5">
              {crumb.icon === 'home' && (
                <Home className="w-3.5 h-3.5" aria-hidden="true" />
              )}
              <span className={isLast ? 'font-semibold text-slate-800' : ''}>
                {crumb.label}
              </span>
            </span>
          );
          return (
            <li key={`${idx}-${crumb.label}`} className="inline-flex items-center gap-1">
              {!isLast && crumb.to ? (
                <Link
                  to={crumb.to}
                  className="text-slate-500 hover:text-slate-900 transition-colors hover:underline underline-offset-4"
                  data-testid={`breadcrumb-link-${crumb.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {content}
                </Link>
              ) : (
                <span
                  className={isLast ? 'text-slate-800' : 'text-slate-500'}
                  aria-current={isLast ? 'page' : undefined}
                  data-testid={isLast ? 'breadcrumb-current' : undefined}
                >
                  {content}
                </span>
              )}
              {!isLast && (
                <ChevronRight
                  className="w-3.5 h-3.5 text-slate-400 flex-shrink-0"
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
